/**
 * Shared hook for all navbar variants.
 * Extracts auth state, active-link detection, dropdown logic, and profile links.
 */
import { useState, useRef, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faFileLines,
  faGears,
  faIdBadge,
  faRobot,
  faShieldHalved,
  faSuitcase,
  faUser,
  faUsers,
  faWrench,
} from '@fortawesome/pro-solid-svg-icons';
import { faClock } from '@fortawesome/pro-regular-svg-icons';
import { useAuth } from '@/auth/useAuth';
import { useAppInfo } from '@/app/useAppInfo';
import { getLabel } from '@/utils/label';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useUnsavedModelGuard } from '@/auth/useUnsavedModelGuard';

export interface NavItem {
  to: string;
  segment: string;
  label: string;
  roles?: string[];
}

export const NAV_ITEMS: NavItem[] = [
  { to: '/dashboard/discover', segment: 'discover', label: 'Discover' },
  { to: '#new-model', segment: 'build', label: 'Build' },
  { to: '/learn', segment: 'learn', label: 'Learn' },
  { to: '/develop', segment: 'develop', label: 'Developer', roles: ['developer', 'publisher', 'admin'] },
  { to: '/viz', segment: 'viz', label: 'Viz' },
  { to: '/brewdb', segment: 'brewdb', label: 'brewDB' },
  { to: '/admin', segment: 'admin', label: 'Admin', roles: ['admin'] },
];

export interface ProfileLink {
  context: string;
  icon: IconDefinition;
  label: string;
}

export const PROFILE_LINKS: ProfileLink[] = [
  { context: 'info', icon: faUser, label: 'User Info' },
  { context: 'credentials', icon: faIdBadge, label: 'Credentials' },
  { context: 'groups', icon: faUsers, label: 'Groups' },
  { context: 'documents', icon: faFileLines, label: 'Documents' },
  { context: 'models', icon: faGears, label: 'Models' },
  { context: 'aiusage', icon: faRobot, label: 'AI Usage' },
  { context: 'jobs', icon: faSuitcase, label: 'Jobs' },
  { context: 'tasks', icon: faClock, label: 'Tasks' },
  { context: 'preferences', icon: faWrench, label: 'Preferences' },
  { context: 'authcreds', icon: faShieldHalved, label: 'Auth Credentials' },
];

/**
 * The Groups workspace entry (jorigin/brewauth#29 GB1). It sits on the RIGHT
 * of the bar, beside the profile and logout controls, for every signed-in
 * user. Below `GROUPS_IN_BAR_QUERY` the bar has no room for it, so it moves
 * into the profile menu (and, below `md`, into the mobile menu) rather than
 * disappearing: it is always reachable.
 *
 * The profile menu already holds the profile's own "Groups" tab, so the
 * fallback entry is named differently: two links with one name and two
 * destinations would be ambiguous.
 */
export const GROUPS_NAV = {
  to: '/groups',
  label: 'Groups',
  menuLabel: 'Groups workspace',
  icon: faUsers,
} as const;

/** The width at which the bar itself has room for the Groups entry (Tailwind's `lg`). */
export const GROUPS_IN_BAR_QUERY = '(min-width: 1024px)';

export function useNavBarState() {
  const { authenticated, userProfile, hasRole, handleLogout } = useAuth();
  const { info } = useAppInfo();
  const location = useLocation();
  // Where matchMedia is unknown, keep the entry in the bar.
  const groupsInBar = useMediaQuery(GROUPS_IN_BAR_QUERY, true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLLIElement>(null);

  const path = location.pathname;
  const isActive = (segment: string) => {
    if (segment === 'discover') return path.includes('/dashboard/discover');
    if (segment === 'build') return path.includes('/dashboard/build') || path.startsWith('/build/');
    if (segment === 'learn') return path.startsWith('/learn');
    if (segment === 'viz') return path.startsWith('/viz');
    if (segment === 'brewdb') return path.startsWith('/brewdb');
    if (segment === 'develop') return path.startsWith('/develop');
    if (segment === 'admin') return path.startsWith('/admin');
    if (segment === 'users') return path.startsWith('/users');
    if (segment === 'groups') return path === '/groups' || path.startsWith('/groups/');
    return false;
  };

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setUserDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const capcoEnabled = info?.capco?.enabled;

  const profileLink = (context: string) =>
    userProfile ? `/users/profile/${userProfile._id}/${context}` : '#';

  const userLabel = getLabel(userProfile?.metadata?.label);

  const visibleNavItems = useMemo(() => NAV_ITEMS.filter(item => {
    if (!item.roles) return true;
    return item.roles.some(role => hasRole(role));
  }), [hasRole]);

  // Asks first when the open model has unsaved changes (#2272); the caller
  // renders `logoutConfirm`.
  const { guard: guardUnsavedModel, dialog: logoutConfirm } = useUnsavedModelGuard();
  const doLogout = () => {
    guardUnsavedModel(() => void handleLogout());
  };

  return {
    authenticated, info, userProfile,
    mobileMenuOpen, setMobileMenuOpen,
    userDropdownOpen, setUserDropdownOpen,
    dropdownRef, isActive, path,
    capcoEnabled, profileLink, userLabel,
    visibleNavItems, doLogout, logoutConfirm,
    groupsInBar,
  };
}
